/**
 * BrandTokens — the single source of *appearance* ("skin") a CV template reads.
 *
 * Step 3 of the CV-template refactor (see /CV_TEMPLATES_PLAN.md). Colors are named
 * by *role* (what they are for), not by a generic primary/secondary slot, so that
 * different templates can express the same profile very differently.
 *
 * Only `colors.sectionTitle` is consumed today (by ClassicTemplate). The remaining
 * roles and the typography/style fields are scaffolding: they establish the shape
 * so later templates (step 4+) and custom fonts can plug in without a re-design.
 * Customer-facing controls ("knobs") over these tokens are intentionally deferred.
 */

export interface BrandColors {
  /** Brand primary — headers/emphasis in templates that use it. */
  primary: string;
  /** Accent — skill tags, links, decorative marks. */
  accent: string;
  /** Section headings ("Erfarenhet", "Kompetenser"). Today's `secondary`. */
  sectionTitle: string;
  /** Body text. */
  text: string;
  /** Secondary text — dates, captions. */
  muted: string;
  /** Dividers / borders. */
  line: string;
  /** Page background. */
  background: string;
}

export interface BrandTypography {
  /** Font family for headings. Undefined = inherit the app default. */
  headingFont?: string;
  /** Font family for body text. Undefined = inherit the app default. */
  bodyFont?: string;
  /** Base body font size. */
  baseSize: string;
  /** Modular scale ratio for heading sizes. */
  scale: number;
}

export type SectionTitleStyle = "bullet" | "underline" | "plain";
export type PhotoShape = "round" | "square";
export type Density = "compact" | "regular" | "airy";
export type LogoPlacement = "top" | "header" | "none";

export interface BrandTokens {
  colors: BrandColors;
  typography: BrandTypography;
  sectionTitleStyle: SectionTitleStyle;
  photo: PhotoShape;
  density: Density;
  logoPlacement: LogoPlacement;
}

/**
 * Classic defaults. `colors.sectionTitle` (#6b7280) matches the ThemeContext
 * fallback for `secondary` when no organization color is set, so a profile with
 * no org theme renders identically to before.
 */
export const defaultTokens: BrandTokens = {
  colors: {
    primary: "#3b82f6",
    accent: "#3b82f6",
    sectionTitle: "#6b7280",
    text: "#111827",
    muted: "#6b7280",
    line: "#e5e7eb",
    background: "#ffffff",
  },
  typography: {
    headingFont: undefined,
    bodyFont: undefined,
    baseSize: "14px",
    scale: 1.2,
  },
  sectionTitleStyle: "bullet",
  photo: "square",
  density: "regular",
  logoPlacement: "top",
};
